/**
 * Standalone Chat Shell - Vitreous Chat as its own site (vitreous.support/chat)
 * Minimal header: logo, "Open main site", user menu. No dashboard/tickets sidebar.
 * Same account works for vitreous.support and vitreous.support/chat.
 */

import React, { useEffect } from 'react';
import { Layout, Avatar, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { LogoutOutlined, HomeOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Chat from '../pages/Chat';
import './StandaloneChatShell.css';

const { Header, Content } = Layout;
const { Text } = Typography;

export const StandaloneChatShell: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Vitreous Chat - Team chat';
    return () => {
      document.title = 'Vitreous Support - Support Ticket Platform';
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login?returnUrl=%2Fchat');
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'main-site',
      icon: <HomeOutlined />,
      label: 'Open main site',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      danger: true,
      onClick: handleLogout,
    },
  ];

  if (!user) {
    return null; // AuthProvider will show login
  }

  return (
    <Layout className="standalone-chat-shell" style={{ minHeight: '100vh' }}>
      <Header className="standalone-chat-header">
        <a href="/" className="standalone-chat-logo" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          <span className="standalone-chat-logo-text">Vitreous Chat</span>
        </a>
        <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={['click']}>
          <div className="standalone-chat-user">
            <Text strong className="standalone-chat-user-name">
              {user.first_name} {user.last_name}
            </Text>
            <Avatar src={user.avatar_url} size="small" style={{ backgroundColor: '#5856d6' }}>
              {user.first_name?.[0]}
              {user.last_name?.[0]}
            </Avatar>
          </div>
        </Dropdown>
      </Header>
      <Content className="standalone-chat-content">
        <Chat />
      </Content>
    </Layout>
  );
};

export default StandaloneChatShell;
